import { useEffect, useState } from "react";

import {
  useMintCode, useTelegramState, useTelegramWebhook, useUnlinkTelegram,
} from "../api/queries";
import type { TelegramWebhookInfo } from "../api/types";
import { useSession } from "../auth";
import "./pages.css";
import { Empty } from "../components/Empty";
import { ErrorBand } from "../components/ErrorBand";
import { Panel } from "../components/Panel";
import { Skeleton } from "../components/Skeleton";
import { TitleBand } from "../components/TitleBand";
import { plural, when } from "../format";
import type { CodeLife, LinkView } from "./telegramLink";
import {
  blockedMessage, botHandle, codeLife, linkCommand, linkView,
} from "./telegramLink";

/** The word for each state, in the title band.
 *
 *  Every state on this page carries one, the rule ModePill follows and the
 *  watchlist's "hit" row follows. Here it is not a redundant encoding beside a
 *  colour — there is no colour on this page at all, the hue budget being spent
 *  — so the word is the only encoding there is. */
const WORD: Record<LinkView["kind"], string> = {
  unconfigured: "not configured",
  unlinked: "not linked",
  linked: "linked",
  blocked: "blocked",
};

/** The code and what to do with it.
 *
 *  Shared by the unlinked state and the blocked one, because unblocking the
 *  bot is only half the repair: `consume_code` re-links the row and clears
 *  `disabled_at`, so somebody whose chat blocked the bot needs a fresh code
 *  exactly as much as somebody who has never linked. Typed off the hook the
 *  way Watch's TargetCell takes its mutation, so this module keeps its
 *  distance from react-query.
 *
 *  `life` is computed by the caller rather than here: the same value decides
 *  whether the settings query polls, and two clocks disagreeing about whether
 *  a code is still good is exactly the confusion this page must not add. */
function MintCode({ mint, life }: {
  mint: ReturnType<typeof useMintCode>; life: CodeLife | null;
}) {
  const code = mint.data;
  const handle = code === undefined ? null : botHandle(code.bot_name);
  // A 503 means this deployment lost its bot between the page load and the
  // click — it was restarted — not that the reader did anything wrong.
  const status = (mint.error as { status?: number } | null)?.status;

  return (
    <>
      <button type="button" disabled={mint.isPending} onClick={() => mint.mutate()}>
        {code === undefined ? "Get a link code" : "Get another code"}
      </button>

      {mint.isError ? (
        <ErrorBand
          message="Could not get a code."
          hint={status === 503
            ? "This deployment has no Telegram bot configured any more. Reload the page."
            : "Nothing has changed. Try again in a moment."}
        />
      ) : null}

      {code === undefined || life === null ? null : life.live ? (
        <>
          <p>
            Send this to {handle === null ? "the bot" : <code>{handle}</code>} in
            Telegram, in a direct message:
          </p>
          <code className="command">{linkCommand(code.code)}</code>
          <p className="dim">
            It expires in {plural(life.minutes, "minute")}.
            {handle === null
              ? " This deployment could not learn its bot's name at boot, so ask"
                + " whoever set it up which bot to send it to."
              : ""}
          </p>
          {/* Kept, for a reason that has nothing to do with parsing: the
              bot's confirmation names the account it just linked, and a group
              is a room full of people who would read it. */}
          <p className="dim">
            In a direct message and not a group — the bot's reply names your
            account.
          </p>
        </>
      ) : (
        <p className="dim">That code has expired. Ask for another.</p>
      )}
    </>
  );
}

/** What the panel says in each state. Early returns rather than a chain of
 *  ternaries in the markup: the states are the page, and they should be as
 *  readable here as they are in telegramLink.ts.
 *
 *  Exported for Settings.test.tsx, which renders it. `renderToStaticMarkup`
 *  needs no DOM and so runs under this suite's `environment: "node"` with
 *  nothing new installed — and the states that depend on a *mutation* holding
 *  a code cannot be reached through the page itself, because reaching them
 *  takes a click. Those are exactly the states that carry the instructions
 *  this whole page exists to give, so they are worth a seam. */
export function LinkBody({ view, mint, unlink, life }: {
  view: LinkView;
  mint: ReturnType<typeof useMintCode>;
  unlink: ReturnType<typeof useUnlinkTelegram>;
  life: CodeLife | null;
}) {
  // And nothing else: no button that cannot work and no code that could never
  // be delivered. `configured` is the server's own linking_configured, so the
  // mint route would answer this deployment's reader a 503. This is only ever
  // an account with no chat of its own — one that has a chat is told about it
  // whatever the deployment can or cannot do; see linkView.
  if (view.kind === "unconfigured") {
    return <Empty>This deployment has no Telegram bot configured.</Empty>;
  }

  if (view.kind === "unlinked") {
    return (
      <div className="settings">
        <p>
          Link a Telegram chat and this account is told when a watched price
          crosses its target, and when the deal hunter finishes a deal.
        </p>
        <MintCode mint={mint} life={life} />
      </div>
    );
  }

  return (
    <div className="settings">
      <p>
        This account's Telegram chat is <code>{view.chatId}</code>
        {view.linkedAt === null ? "" : `, linked ${when(view.linkedAt)}`}.
      </p>
      {view.kind === "blocked" ? (
        <>
          {/* The reason is Telegram's own words, carried through rather than
              translated: it is the only account anybody gets of what happened.
              This page is the only place that can explain this state — nothing
              is being delivered, so no notification can say so. */}
          <ErrorBand
            message={blockedMessage(view.reason)}
            hint={view.canLink
              ? "Nothing has been sent since. Unblock the bot in Telegram, then"
                + " send it a new code to link this chat again."
              : "Nothing has been sent since. Repairing this needs a new link"
                + " code, which this deployment cannot mint."}
          />
          {view.canLink ? <MintCode mint={mint} life={life} /> : null}
        </>
      ) : view.canLink ? (
        <p className="dim">
          Watch hits and finished deals are sent there.
        </p>
      ) : null}

      {/* Said whenever the deployment cannot mint, and said carefully. What is
          certainly true is that no new link can be made here. What this page
          cannot know is whether messages are still going out: sending needs
          only the bot token, while `configured` also wants the webhook secret
          and the base URL, so this state covers both a deployment that is
          still messaging this chat and one that has no bot at all. Unlink is
          the control that settles it either way, which is why it is never
          hidden. */}
      {view.canLink ? null : (
        <p className="dim">
          New links cannot be made on this deployment — its Telegram
          configuration is incomplete. This chat is on record here, and
          unlinking it still works.
        </p>
      )}
      <button
        type="button"
        disabled={unlink.isPending}
        // mint.reset() with it: without that, unlinking leaves the previous
        // code sitting in the mutation's data, and the unlinked panel this
        // turns into would paint it as though it had just been minted.
        onClick={() => unlink.mutate(undefined, { onSuccess: () => mint.reset() })}
      >
        Unlink
      </button>
      {unlink.isError ? (
        <ErrorBand
          message="Could not unlink."
          hint="Nothing has changed. Reload and try again."
        />
      ) : null}
    </div>
  );
}

function LinkSection() {
  const mint = useMintCode();
  const unlink = useUnlinkTelegram();

  const [, tick] = useState(0);
  const life = mint.data === undefined ? null : codeLife(mint.data.expires_at);
  // Polled only while a code could still be spent. The link itself happens in
  // another application — the reader sends the code to the bot and comes back
  // — and without this the page could never say that it worked.
  const state = useTelegramState({ poll: life?.live === true });
  const view = state.data === undefined ? null : linkView(state.data);

  // The one thing on this page that changes with nobody clicking: a code stops
  // working ten minutes after it was minted. react-query's structural sharing
  // means an unchanged poll response does not re-render the page, so the
  // countdown gets a clock of its own — which runs only while a code is on
  // screen and stops itself when that code dies.
  useEffect(() => {
    const code = mint.data;
    if (code === undefined) return;
    const timer = setInterval(() => {
      tick((n) => n + 1);
      if (!codeLife(code.expires_at).live) clearInterval(timer);
    }, 15_000);
    return () => clearInterval(timer);
  }, [mint.data]);

  // A code that has been spent leaves the screen, which stops the poll and the
  // clock with it. Keyed on a *working* link rather than on `linked`, because
  // the blocked state is linked already and is minting a code to repair
  // itself: resetting on `linked` alone would delete that code the instant it
  // arrived.
  const working = view?.kind === "linked";
  useEffect(() => {
    if (working && mint.data !== undefined) mint.reset();
  }, [working, mint.data]);

  // `isLoadingError`, not `isError`, for the reason Watch.tsx spells out at
  // length: this page polls, and a background poll that fails must not throw
  // away a state the page has already read correctly. Nothing is said about a
  // failed poll either — the only thing it delays is noticing a link that has
  // landed, and the next one is ten seconds away.
  if (state.isLoadingError) {
    return (
      <>
        <TitleBand title="Telegram" />
        <ErrorBand
          message="Could not load your Telegram settings."
          hint="The server may be restarting. Reload in a moment."
        />
      </>
    );
  }
  if (view === null) {
    return <><TitleBand title="Telegram" /><Skeleton rows={4} /></>;
  }

  return (
    <>
      <TitleBand title="Telegram" note={WORD[view.kind]} />
      <Panel primary>
        <LinkBody view={view} mint={mint} unlink={unlink} life={life} />
      </Panel>
    </>
  );
}

/** Deliveries this server refused because their secret did not match.
 *
 *  Nothing else on this panel can show this, and that is the point of it. A
 *  refused delivery is answered 200 — it has to be, or Telegram redelivers
 *  junk with backoff — so Telegram records it as delivered and drops it: the
 *  backlog below stays empty and the last error stays blank while every real
 *  delivery is discarded. A figure here is therefore the only evidence that
 *  the two ends disagree about the webhook secret, which is the failure this
 *  panel exists to catch and is reachable with nobody making a mistake — a
 *  boot whose setWebhook was throttled leaves Telegram holding the old one.
 *
 *  A band rather than a figure, because it is never ordinary: zero is the only
 *  healthy value, and anything else is deliveries being thrown away right now.
 *  The count itself is in the summary below, where zero can be read as news. */
function Refused({ count }: { count: number }) {
  if (count === 0) return null;
  return (
    <ErrorBand
      message={`${plural(count, "delivery", "deliveries")} refused since this`
               + " server started: the secret they carried did not match."}
      hint={"Telegram is told 200 and drops each one, so nothing else here will"
            + " ever show it. The two ends disagree about"
            + " TELEGRAM_WEBHOOK_SECRET — restart the API, which is what"
            + " registers the secret this deployment has now."}
    />
  );
}

function WebhookReport({ info }: { info: TelegramWebhookInfo }) {
  if (!info.ok) {
    return (
      <>
        <ErrorBand
          message="Telegram could not be asked how delivery is going."
          hint={info.detail === "" ? undefined : info.detail}
        />
        {/* Counted here and not by Telegram, so it is still worth saying when
            Telegram cannot be reached at all — a rotated token and a rotated
            secret tend to arrive together. */}
        <Refused count={info.rejected_since_boot} />
      </>
    );
  }
  return (
    <>
      {info.url_set ? null : (
        <ErrorBand
          message="No webhook is registered, so nothing sent to the bot will ever arrive."
          hint={"The server registers it at boot from the public base URL. Check"
                + " that setting and restart the API."}
        />
      )}
      <Refused count={info.rejected_since_boot} />
      <div className="settings">
        <dl className="cost-summary">
          <div>
            <dt>Endpoint</dt>
            <dd>{info.url_set ? "registered" : "not registered"}</dd>
          </div>
          <div>
            <dt>Waiting</dt>
            <dd>{info.pending_update_count}</dd>
          </div>
          <div>
            <dt>Refused</dt>
            <dd>{info.rejected_since_boot}</dd>
          </div>
        </dl>
        <p className="dim">
          {info.last_error_message === ""
            ? "Telegram reports no delivery error."
            : `Last delivery error${info.last_error_date === null
                ? "" : `, ${when(info.last_error_date)}`}: ${info.last_error_message}`}
        </p>
        {info.pending_update_count > 0 ? (
          <p className="dim">
            Telegram is holding those updates because this server has not taken
            them — which is what a rejected or unreachable endpoint looks like
            from the outside.
          </p>
        ) : null}
      </div>
    </>
  );
}

/** Telegram's own account of how delivery is going. Owners only.
 *
 *  It is here because linking cannot be exercised on a laptop at all —
 *  Telegram will not deliver to one — so the first real link on a deployment
 *  is also the first test of the whole path, and `last_error_message` is the
 *  only place that attempt says why it did not work.
 *
 *  Rendered even when linking is unconfigured, which is the one place this
 *  page and `linkView` disagree on purpose. The route is gated on the bot
 *  token alone rather than on linking_configured, precisely so a
 *  half-configured deployment can still be diagnosed; going quiet here exactly
 *  when the feature is broken would take the diagnosis away in the only case
 *  anybody would open this panel. */
function WebhookPanel() {
  const info = useTelegramWebhook();
  // A 503 is an answer and not a fault: it means no bot token here, which is
  // the deployment saying the feature was never set up.
  const status = (info.error as { status?: number } | null)?.status;

  return (
    <>
      <TitleBand title="Webhook" note="owner only" />
      {info.isLoading ? <Skeleton rows={3} /> : (
        <Panel>
          {info.isError ? (
            status === 503 ? (
              <Empty>
                No bot token on this deployment, so Telegram has nothing to report.
              </Empty>
            ) : (
              <ErrorBand
                message="Could not ask Telegram about the webhook."
                hint="The server may be restarting. Reload in a moment."
              />
            )
          ) : info.data ? (
            <WebhookReport info={info.data} />
          ) : null}
        </Panel>
      )}
    </>
  );
}

/** The only page in this app about the account rather than the market, and
 *  Telegram is the only thing on it: everything else an account has is either
 *  fixed at sign-up or a property of what it is reading. */
export function Settings() {
  const { me } = useSession();

  return (
    <>
      <LinkSection />
      {/* Not a 403 branch inside the panel and not an `enabled` flag on the
          query: an owner-only route answers everybody else 403, which is an
          ordinary answer rather than something to render, so a viewer's
          browser never issues the request at all. */}
      {me?.role === "owner" ? <WebhookPanel /> : null}
    </>
  );
}
